/**
 * @fileoverview Contract-First API Client Generator
 * @module @monorepo/api-client/core
 *
 * Generic, type-safe API client that generates methods from API contracts.
 * Provides 100% type safety with automatic request/response validation.
 *
 * Features:
 * - Automatic method generation from contracts
 * - Type-safe request/response handling
 * - Optional Zod validation
 * - Typed error handling
 * - Path parameter replacement
 */

import type { z } from 'zod';
import { HttpClient, HttpClientError } from './http-client';
import type { ErrorCode } from '@monorepo/types';

// ============================================================================
// Types
// ============================================================================

/**
 * API Contract Route Definition
 */
export interface ContractRoute<
	TBody = unknown,
	TResponse = unknown,
	TQuery = unknown,
	TParams = unknown,
> {
	readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	readonly path: string;
	readonly body?: z.ZodType<TBody>;
	readonly response: z.ZodType<TResponse>;
	readonly query?: z.ZodType<TQuery>;
	readonly params?: z.ZodType<TParams>;
}

/**
 * Extract contract structure from createContract result
 */
export type ExtractContract<T> = T extends { base: string; routes: infer R }
	? { base: string; routes: R }
	: never;

/**
 * Contract Client Options
 */
export interface ContractClientOptions {
	/** Enable request validation with Zod (default: false) */
	readonly validateRequests?: boolean;
	/** Enable response validation with Zod (default: false) */
	readonly validateResponses?: boolean;
	/** Throw on validation errors (default: true) */
	readonly throwOnValidationError?: boolean;
}

/**
 * API Client Error
 * Extends HttpClientError with additional context from ApiErrorResponse
 * Provides enhanced error information for debugging
 */
export class ApiClientError extends HttpClientError {
	public readonly details?: Record<string, unknown>;
	public readonly validationErrors?: Array<{
		field: string;
		message: string;
		code?: string;
	}>;
	public readonly requestBody?: unknown;
	public readonly requestQuery?: unknown;
	public readonly timestamp: string;

	constructor(
		message: string,
		statusCode: number,
		code: string,
		public readonly endpoint: string,
		public readonly method: string,
		response?: unknown,
		requestContext?: {
			body?: unknown;
			query?: unknown;
		},
	) {
		super(message, statusCode, code as ErrorCode, response, { endpoint, method });
		this.name = 'ApiClientError';
		this.timestamp = new Date().toISOString();
		this.requestBody = requestContext?.body;
		this.requestQuery = requestContext?.query;
		Object.setPrototypeOf(this, ApiClientError.prototype);

		// Extract additional details from ApiErrorResponse if available
		if (response && typeof response === 'object' && response !== null) {
			const data = response as Record<string, unknown>;

			// Handle ApiErrorResponse structure
			if (data.success === false && typeof data.error === 'object' && data.error !== null) {
				const errorObj = data.error as Record<string, unknown>;

				// Extract details
				if (errorObj.details && typeof errorObj.details === 'object') {
					this.details = errorObj.details as Record<string, unknown>;
				}

				// Extract validation errors
				if (Array.isArray(errorObj.validationErrors)) {
					this.validationErrors = errorObj.validationErrors as Array<{
						field: string;
						message: string;
						code?: string;
					}>;
				}
			}
		}
	}

	/**
	 * Serialize error to JSON for logging and debugging
	 */
	toJSON() {
		return {
			name: this.name,
			message: this.message,
			statusCode: this.statusCode,
			code: this.code,
			endpoint: this.endpoint,
			method: this.method,
			timestamp: this.timestamp,
			details: this.details,
			validationErrors: this.validationErrors,
			requestBody: this.requestBody,
			requestQuery: this.requestQuery,
			response: this.response,
			stack: this.stack,
		};
	}
}

// ============================================================================
// Path Parameter Replacement
// ============================================================================

/**
 * Replace path parameters with actual values
 *
 * @example
 * replacePath('/users/:id/posts/:postId', { id: '123', postId: '456' })
 * // Returns: '/users/123/posts/456'
 */
export function replacePath(path: string, params: Record<string, string | number>): string {
	let result = path;
	for (const [key, value] of Object.entries(params)) {
		result = result.replace(`:${key}`, String(value));
	}
	return result;
}

// ============================================================================
// Contract Client Base Class
// ============================================================================

/**
 * Contract-First API Client
 *
 * Base class for creating type-safe API clients from contracts.
 * Provides automatic request/response handling with optional validation.
 *
 * @example
 * ```typescript
 * class UsersClient extends ContractClient<typeof USERS_API_CONTRACT> {
 *   constructor(httpClient: HttpClient) {
 *     super(httpClient, USERS_API_CONTRACT, { validateResponses: true });
 *   }
 *
 *   async getUser(id: string) {
 *     return this.execute('getOne', { params: { id } });
 *   }
 * }
 * ```
 */
export abstract class ContractClient<
	TContract extends { base: string; routes: Record<string, unknown> },
> {
	constructor(
		protected readonly httpClient: HttpClient,
		protected readonly contract: TContract,
		protected readonly options: ContractClientOptions = {},
	) {}

	/**
	 * Execute a contract route
	 *
	 * @template TRoute - Route name from contract
	 * @param routeName - Name of the route to execute
	 * @param options - Request options
	 * @returns Promise resolving to the full wrapped API response
	 */
	protected async execute<TRoute extends keyof TContract['routes']>(
		routeName: TRoute,
		options: {
			readonly body?: unknown;
			readonly query?: unknown;
			readonly params?: Record<string, string | number>;
			readonly headers?: Record<string, string>;
		} = {},
	): Promise<
		TContract['routes'][TRoute] extends { response: z.ZodType<infer R> } ? R : never
	> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const route = this.contract.routes[routeName as string] as any;
		if (!route) {
			throw new Error(`Route '${String(routeName)}' not found in contract`);
		}

		// Validate request body if enabled
		if (this.options.validateRequests && route.body && options.body) {
			try {
				route.body.parse(options.body);
			} catch (error) {
				if (this.options.throwOnValidationError !== false) {
					const errorMessage = error instanceof Error ? error.message : String(error);
					throw new ApiClientError(
						`Request validation failed: ${errorMessage}`,
						400,
						'VALIDATION_ERROR',
						route.path,
						route.method,
						undefined,
						{ body: options.body },
					);
				}
			}
		}

		// Validate query parameters if enabled
		if (this.options.validateRequests && route.query && options.query) {
			try {
				route.query.parse(options.query);
			} catch (error) {
				if (this.options.throwOnValidationError !== false) {
					const errorMessage = error instanceof Error ? error.message : String(error);
					throw new ApiClientError(
						`Query validation failed: ${errorMessage}`,
						400,
						'VALIDATION_ERROR',
						route.path,
						route.method,
						undefined,
						{ query: options.query },
					);
				}
			}
		}

		// Build path with parameters
		let path = `${this.contract.base}${route.path}`;
		if (options.params) {
			path = replacePath(path, options.params);
		}

		// Execute request
		try {
			const response = await this.httpClient.request<unknown>(path, {
				method: route.method,
				data: options.body,
				config: {
					params: options.query as Record<string, unknown>,
					headers: options.headers,
				},
			});

			// Validate response if enabled
			// Note: response IS the full backend response (already wrapped as ApiResponse<T>)
			if (this.options.validateResponses && response) {
				try {
					route.response.parse(response);
				} catch (error) {
					if (this.options.throwOnValidationError !== false) {
						// Format Zod validation errors for better readability
						let errorMessage = error instanceof Error ? error.message : String(error);

						try {
							const zodErrors = JSON.parse(errorMessage);
							if (Array.isArray(zodErrors) && zodErrors.length > 0) {
								// Format as human-readable field errors
								const fieldErrors = zodErrors.map((err: { path?: string[], message: string, received?: unknown }) => {
									const fieldPath = err.path ? err.path.join('.') : 'unknown';
									const received = err.received !== undefined ? ` (received: ${err.received})` : '';
									return `  • Field '${fieldPath}': ${err.message}${received}`;
								}).join('\n');
								errorMessage = `Response validation failed for ${route.method} ${path}:\n${fieldErrors}`;
							}
						} catch {
							// If parsing fails, use original error message
							errorMessage = `Response validation failed: ${errorMessage}`;
						}

						throw new ApiClientError(
							errorMessage,
							500,
							'VALIDATION_ERROR',
							path,
							route.method,
							response,
							{ body: options.body, query: options.query },
						);
					}
				}
			}

			// Return the full wrapped response from backend
			// HttpClient.request() returns ApiResponse<T> directly
			return response as TContract['routes'][TRoute] extends { response: z.ZodType<infer R> }
				? R
				: never;
		} catch (error) {
			// Re-throw HttpClientError as ApiClientError with additional context
			if (error instanceof HttpClientError) {
				throw new ApiClientError(
					error.message,
					error.statusCode,
					error.code,
					path,
					route.method,
					error.response,
					{ body: options.body, query: options.query },
				);
			}
			throw error;
		}
	}

	/**
	 * Get the base path for this client
	 */
	getBasePath(): string {
		return this.contract.base;
	}
}
